import React, { useState, useEffect } from "react";
import axios from "axios";
import { submit_gd_FormData, updateGuard } from "../api/vipform";
import { toast } from "react-toastify";

const BASE_URL = process.env.REACT_APP_BASE_URL;

export default function GuardForm() {
  const [guardformData, setGuardFormData] = useState({
    id: "",
    name: "",
    email: "",
    gender: "",
    pnNumber: "",
    adharNo: "",
    username: "",
    password: "",
    rank: "",
    experience: "",
    contactno: "",
    picture: "",
    status: "Inactive",
  });

  const [ranks, setRanks] = useState([]);
  const [isEditMode, setIsEditMode] = useState(false);
  const [otherRank, setOtherRank] = useState("");
  const [showOtherRank, setShowOtherRank] = useState(false);
  const [emailError, setEmailError] = useState("");
  const [contactError, setContactError] = useState("");
  const [loading, setLoading] = useState(false);
  const [imageFile, setImageFile] = useState(null);

  /* FETCH RANKS */
  useEffect(() => {
    const fetchRanks = async () => {
      try {
        const role = localStorage.getItem("role");
        const token = localStorage.getItem(`${role}Token`);
        const res = await axios.get(`${BASE_URL}/api/officer/unique-ranks`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setRanks(res.data || []);
      } catch {
        toast.error("Unable to load ranks!");
        setRanks([]);
      }
    };
    fetchRanks();
  }, []);

  /* INPUT HANDLER */
  const handleChange = (e) => {
    const { name, value } = e.target;

    if (name === "email") {
      setGuardFormData((p) => ({ ...p, email: value }));
      const r = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      setEmailError(r.test(value) || value === "" ? "" : "Invalid email format");
      return;
    }

    if (name === "contactno") {
      if (/^\d{0,10}$/.test(value)) {
        setGuardFormData((p) => ({ ...p, contactno: value }));
        setContactError(value.length === 10 || value === "" ? "" : "Contact must be 10 digits");
      }
      return;
    }

    if (name === "adharNo") {
      if (/^\d{0,12}$/.test(value)) {
        setGuardFormData((p) => ({ ...p, adharNo: value }));
      }
      return;
    }

    if (name === "experience") {
      if (/^\d*$/.test(value)) {
        setGuardFormData((p) => ({ ...p, experience: value }));
      }
      return;
    }

    if (name === "rank") {
      setGuardFormData((p) => ({ ...p, rank: value }));
      setShowOtherRank(value === "Other");
      if (value !== "Other") setOtherRank("");
      return;
    }

    setGuardFormData((p) => ({ ...p, [name]: value }));
  };

  const handleImageSelect = (e) => {
    setImageFile(e.target.files[0]);
  };

  const uploadImage = async (guardId) => {
    if (!imageFile) return;

    const token = localStorage.getItem("adminToken");
    const formData = new FormData();
    formData.append("imaged", imageFile);

    await axios.post(
      `${BASE_URL}/api/profile/upload/${guardId}/guard`,
      formData,
      {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "multipart/form-data",
        },
      }
    );
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (emailError || contactError) {
      toast.error("Please fix all errors!");
      return;
    }

    if (!imageFile && !isEditMode) {
      toast.error("Please select profile picture!");
      return;
    }

    const finalData = {
      ...guardformData,
      rank: guardformData.rank === "Other" ? otherRank : guardformData.rank,
      status: "Inactive",
    };

    try {
      setLoading(true);

      if (isEditMode) {
        await updateGuard(guardformData.id, finalData);
        if (imageFile) await uploadImage(guardformData.id);
        toast.success("Officer details updated successfully!");
      } else {
        const res = await submit_gd_FormData(finalData);
        const guardId = res?.data?.id || res?.id;
        if (!guardId) {
          toast.error("Officer ID not received!");
          return;
        }
        await uploadImage(guardId);
        toast.success("Officer registered with profile picture!");
      }

      resetForm();
    } catch (e) {
      toast.error(e?.response?.data?.message || "Submission failed");
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setGuardFormData({
      id: "",
      name: "",
      email: "",
      gender: "",
      pnNumber: "",
      adharNo: "",
      username: "",
      password: "",
      rank: "",
      experience: "",
      contactno: "",
      picture: "",
      status: "Inactive",
    });
    setOtherRank("");
    setShowOtherRank(false);
    setEmailError("");
    setContactError("");
    setIsEditMode(false);
    setImageFile(null);
  };

  return (
    <>
      <div className="guardform-wrapper">
        <div className="guardform-card">
          {/* Header */}
          <div className="card-header">
            <div className="header-content">
              <h1 className="title">
                {isEditMode ? "Edit Officer Details" : "Officer Registration"}
              </h1>
              <p className="subtitle">
                {isEditMode
                  ? "Update security officer information securely."
                  : "Register new security personnel with complete details."}
              </p>
            </div>
            <div className={`mode-badge ${isEditMode ? "edit" : "register"}`}>
              {isEditMode ? "Edit Mode" : "New Registration"}
            </div>
          </div>

          {/* Form Body */}
          <div className="card-body">
            <form onSubmit={handleSubmit}>
              <div className="form-grid">
                {/* Regular Fields */}
                <div className="form-group">
                  <label>Full Name <span className="required">*</span></label>
                  <input
                    type="text"
                    name="name"
                    value={guardformData.name}
                    onChange={handleChange}
                    placeholder="Enter full name"
                    required
                  />
                </div>

                <div className="form-group">
                  <label>Email Address <span className="required">*</span></label>
                  <input
                    type="email"
                    name="email"
                    value={guardformData.email}
                    onChange={handleChange}
                    placeholder="officer@example.com"
                    className={emailError ? "error" : ""}
                    required
                  />
                  {emailError && <span className="error-text">{emailError}</span>}
                </div>

                <div className="form-group">
                  <label>PN Number <span className="required">*</span></label>
                  <input
                    type="text"
                    name="pnNumber"
                    value={guardformData.pnNumber}
                    onChange={handleChange}
                    placeholder="Enter PN number"
                    required
                  />
                </div>

                <div className="form-group">
                  <label>Aadhaar Number <span className="required">*</span></label>
                  <input
                    type="text"
                    name="adharNo"
                    value={guardformData.adharNo}
                    onChange={handleChange}
                    placeholder="1234 5678 9012"
                    maxLength="12"
                    required
                  />
                </div>

                <div className="form-group">
                  <label>Username <span className="required">*</span></label>
                  <input
                    type="text"
                    name="username"
                    value={guardformData.username}
                    onChange={handleChange}
                    placeholder="Choose username"
                    required
                  />
                </div>

                <div className="form-group">
                  <label>Password <span className="required">*</span></label>
                  <input
                    type="password"
                    name="password"
                    value={guardformData.password}
                    onChange={handleChange}
                    placeholder="Enter strong password"
                    required
                  />
                </div>

                <div className="form-group">
                  <label>Work Experience (Years) <span className="required">*</span></label>
                  <input
                    type="text"
                    name="experience"
                    value={guardformData.experience}
                    onChange={handleChange}
                    placeholder="e.g. 5"
                    required
                  />
                </div>

                <div className="form-group">
                  <label>Contact Number <span className="required">*</span></label>
                  <input
                    type="tel"
                    name="contactno"
                    value={guardformData.contactno}
                    onChange={handleChange}
                    placeholder="9876543210"
                    maxLength="10"
                    className={contactError ? "error" : ""}
                    required
                  />
                  {contactError && <span className="error-text">{contactError}</span>}
                  <small className="help-text">Must be exactly 10 digits</small>
                </div>

                {/* Gender */}
                <div className="form-group full-width">
                  <label>Gender <span className="required">*</span></label>
                  <select
                    name="gender"
                    value={guardformData.gender}
                    onChange={handleChange}
                    required
                  >
                    <option value="">Select Gender</option>
                    <option value="Male">Male</option>
                    <option value="Female">Female</option>
                    <option value="Other">Other</option>
                  </select>
                </div>

                {/* Rank */}
                <div className="form-group full-width">
                  <label>Officer Rank <span className="required">*</span></label>
                  <select
                    name="rank"
                    value={guardformData.rank}
                    onChange={handleChange}
                    required
                  >
                    <option value="">Select Rank</option>
                    {ranks.map((r, i) => (
                      <option key={i} value={r}>{r}</option>
                    ))}
                    <option value="Other">Other</option>
                  </select>
                </div>

                {/* Custom Rank */}
                {showOtherRank && (
                  <div className="form-group full-width">
                    <label>Custom Rank <span className="required">*</span></label>
                    <input
                      type="text"
                      placeholder="Enter custom rank"
                      value={otherRank}
                      onChange={(e) => setOtherRank(e.target.value)}
                      required
                    />
                  </div>
                )}

                {/* Profile Picture */}
                <div className="form-group full-width">
                  <label>
                    Profile Picture{" "}
                    {isEditMode ? "(Optional for update)" : <span className="required">*</span>}
                  </label>
                  <div className="file-input-wrapper">
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handleImageSelect}
                      required={!isEditMode}
                    />
                    {imageFile && <span className="file-name">{imageFile.name}</span>}
                  </div>
                  {!imageFile && !isEditMode && (
                    <small className="help-text error-text">Required for new registration</small>
                  )}
                </div>
              </div>

              <div className="form-actions">
                <button type="submit" className="submit-button" disabled={loading}>
                  {loading
                    ? "Processing..."
                    : isEditMode
                    ? "Update Officer"
                    : "Register Officer"}
                </button>

                {isEditMode && (
                  <button type="button" className="cancel-button" onClick={resetForm}>
                    Cancel
                  </button>
                )}
              </div>
            </form>
          </div>
        </div>
      </div>

      <style jsx>{`
        .guardform-wrapper {
          min-height: 100vh;
          background: linear-gradient(135deg, #0f172a 0%, #1e293b 100%);
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 20px;
        }

        .guardform-card {
          width: 100%;
          max-width: 1100px;
          background: rgba(30, 41, 59, 0.9);
          backdrop-filter: blur(12px);
          border-radius: 20px;
          overflow: hidden;
          box-shadow: 0 20px 40px rgba(0, 0, 0, 0.3);
          border: 1px solid rgba(59, 130, 246, 0.3);
        }

        .card-header {
          background: linear-gradient(135deg, #1e3a8a, #3b82f6);
          padding: 40px;
          color: white;
          position: relative;
        }

        .header-content .title {
          font-size: 2rem;
          font-weight: 700;
          margin-bottom: 0.5rem;
          color:#fff;
        }

        .header-content .subtitle {
          font-size: 1.1rem;
          opacity: 0.9;
        }

        .mode-badge {
          position: absolute;
          right: 30px;
          bottom: -20px;
          background: #f59e0b;
          color: white;
          padding: 10px 24px;
          border-radius: 50px;
          font-weight: 600;
          box-shadow: 0 4px 15px rgba(0,0,0,0.2);
        }

        .mode-badge.edit {
          background: #059669;
        }

        .card-body {
          padding: 50px 40px;
        }

        .form-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(320px, 1fr));
          gap: 26px;
        }

        .form-group {
          display: flex;
          flex-direction: column;
        }

        .full-width {
          grid-column: 1 / -1;
        }

        .form-group label {
          font-weight: 600;
          color: white;
          margin-bottom: 8px;
          font-size: 1rem;
        }

        .required {
          color: #f87171;
        }

        .form-group input,
        .form-group select {
          padding: 16px 20px;
          background: rgba(15, 23, 42, 0.6);
          border: 2px solid rgba(59, 130, 246, 0.4);
          border-radius: 12px;
          color: white;
          font-size: 1rem;
          transition: all 0.3s ease;
        }

        .form-group input::placeholder,
        .form-group select::placeholder {
          color: #94a3b8;
        }

        .form-group input:focus,
        .form-group select:focus {
          outline: none;
          border-color: #3b82f6;
          box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.3);
          background: rgba(15, 23, 42, 0.8);
        }

        .form-group input.error {
          border-color: #ef4444;
          box-shadow: 0 0 0 3px rgba(239, 68, 68, 0.2);
        }

        .error-text {
          color: #fca5a5;
          font-size: 0.875rem;
          margin-top: 6px;
        }

        .help-text {
          color: #94a3b8;
          font-size: 0.875rem;
          margin-top: 6px;
        }

        .file-input-wrapper {
          position: relative;
          padding: 16px 20px;
          background: rgba(15, 23, 42, 0.6);
          border: 2px dashed rgba(59, 130, 246, 0.4);
          border-radius: 12px;
          text-align: center;
          transition: all 0.3s ease;
        }

        .file-input-wrapper:hover {
          border-color: #3b82f6;
          background: rgba(15, 23, 42, 0.8);
        }

        .file-input-wrapper input[type="file"] {
          opacity: 0;
          position: absolute;
          top: 0;
          left: 0;
          width: 100%;
          height: 100%;
          cursor: pointer;
        }

        .file-name {
          color: #60a5fa;
          font-size: 0.95rem;
          margin-top: 8px;
          display: block;
        }

        .form-actions {
          margin-top: 40px;
          display: flex;
          gap: 16px;
          flex-wrap: wrap;
        }

        .submit-button {
          flex: 1;
          min-width: 220px;
          background: linear-gradient(135deg, #3b82f6, #2563eb);
          color: white;
          border: none;
          padding: 18px;
          border-radius: 12px;
          font-size: 1.1rem;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.3s ease;
        }

        .submit-button:hover:not(:disabled) {
          transform: translateY(-3px);
          box-shadow: 0 10px 25px rgba(59, 130, 246, 0.4);
        }

        .submit-button:disabled {
          opacity: 0.7;
          cursor: not-allowed;
        }

        .cancel-button {
          background: rgba(100, 116, 139, 0.3);
          color: #e2e8f0;
          border: 2px solid rgba(148, 163, 184, 0.5);
          padding: 18px;
          border-radius: 12px;
          font-size: 1.1rem;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.3s ease;
        }

        .cancel-button:hover {
          background: rgba(148, 163, 184, 0.2);
          transform: translateY(-3px);
        }

        /* Responsive */
        @media (max-width: 768px) {
          .card-header {
            padding: 30px 20px;
          }

          .header-content .title {
            font-size: 1.8rem;
          }

          .mode-badge {
            position: static;
            display: inline-block;
            margin-top: 20px;
          }

          .card-body {
            padding: 30px 20px;
          }

          .form-grid {
            grid-template-columns: 1fr;
            gap: 20px;
          }

          .form-actions {
            flex-direction: column;
          }

          .submit-button,
          .cancel-button {
            width: 100%;
          }
        }

        @media (max-width: 480px) {
          .guardform-wrapper {
            padding: 10px;
          }

          .card-header {
            padding: 25px 15px;
          }

          .header-content .title {
            font-size: 1.6rem;
          }
        }
      `}</style>
    </>
  );
}


// import React, { useState, useEffect } from "react";
// import axios from "axios";
// import { submit_gd_FormData, updateGuard } from "../api/vipform";
// import { toast } from "react-toastify";

// const BASE_URL = process.env.REACT_APP_BASE_URL;

// export default function GuardForm() {
//   const [guardformData, setGuardFormData] = useState({
//     name: "",
//     email: "",
//     gender: "",
//     pnNumber: "",
//     adharNo: "",
//     username: "",
//     password: "",
//     rank: "",
//     experience: "",
//     contactno: "",
//     picture: "",
//     status: "Inactive",
//   });

//   const [ranks, setRanks] = useState([]);
//   const [isEditMode, setIsEditMode] = useState(false);
//   const [otherRank, setOtherRank] = useState("");
//   const [showOtherRank, setShowOtherRank] = useState(false);
//   const [emailError, setEmailError] = useState("");
//   const [contactError, setContactError] = useState("");
//   const [loading, setLoading] = useState(false);

//   /* IMAGE FILE */
//   const [imageFile, setImageFile] = useState(null);

//   /* ---------------- FETCH RANKS ---------------- */
//   useEffect(() => {
//     const fetchRanks = async () => {
//       try {
//         const role = localStorage.getItem("role");
//         const token = localStorage.getItem(`${role}Token`);
//         const res = await axios.get(`${BASE_URL}/api/officer/unique-ranks`, {
//           headers: { Authorization: `Bearer ${token}` },
//         });
//         setRanks(res.data || []);
//       } catch {
//         toast.error("Unable to load ranks!");
//         setRanks([]);
//       }
//     };
//     fetchRanks();
//   }, []);

//   /* ---------------- INPUT HANDLER ---------------- */
//   const handleChange = (e) => {
//     const { name, value } = e.target;

//     if (name === "email") {
//       setGuardFormData((p) => ({ ...p, email: value }));
//       const r = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
//       setEmailError(r.test(value) || value === "" ? "" : "Invalid email format");
//       return;
//     }

//     if (name === "contactno") {
//       if (/^\d{0,10}$/.test(value)) {
//         setGuardFormData((p) => ({ ...p, contactno: value }));
//         setContactError(value.length === 10 || value === "" ? "" : "Contact must be 10 digits");
//       }
//       return;
//     }

//     if (name === "adharNo") {
//       if (/^\d{0,12}$/.test(value)) {
//         setGuardFormData((p) => ({ ...p, adharNo: value }));
//       }
//       return;
//     }

//     if (name === "rank") {
//       setGuardFormData((p) => ({ ...p, rank: value }));
//       setShowOtherRank(value === "Other");
//       if (value !== "Other") setOtherRank("");
//       return;
//     }

//     setGuardFormData((p) => ({ ...p, [name]: value }));
//   };

//   /* ---------------- IMAGE SELECT ---------------- */
//   const handleImageSelect = (e) => {
//     setImageFile(e.target.files[0]);
//   };

//   /* ---------------- IMAGE UPLOAD ---------------- */
//   const uploadImage = async (guardId) => {
//     if (!imageFile) return;

//     const token = localStorage.getItem("adminToken");
//     const formData = new FormData();
//     formData.append("imaged", imageFile);

//     await axios.post(
//       `${BASE_URL}/api/profile/upload/${guardId}/guard`,
//       formData,
//       {
//         headers: {
//           Authorization: `Bearer ${token}`,
//           "Content-Type": "multipart/form-data",
//         },
//       }
//     );
//   };

//   /* ---------------- SUBMIT ---------------- */
//   const handleSubmit = async (e) => {
//     e.preventDefault();

//     if (emailError || contactError) {
//       toast.error("Please fix all errors!");
//       return;
//     }

//     if (!imageFile && !isEditMode) {
//       toast.error("Please select profile picture!");
//       return;
//     }

//     const finalData = {
//       ...guardformData,
//       rank: guardformData.rank === "Other" ? otherRank : guardformData.rank,
//       status: "Inactive",
//     };

//     try {
//       setLoading(true);

//       let guardId;
//       if (isEditMode) {
//         await updateGuard(guardformData.id, finalData);
//         toast.success("Officer details updated successfully!");
//         if (imageFile) await uploadImage(guardformData.id);
//       } else {
//         const res = await submit_gd_FormData(finalData);
//         guardId = res?.data?.id || res?.id;
//         if (!guardId) {
//           toast.error("Officer ID not received!");
//           return;
//         }
//         await uploadImage(guardId);
//         toast.success("Officer registered with profile picture!");
//       }

//       resetForm();
//     } catch (e) {
//       toast.error(e?.response?.data?.message || "Submission failed");
//     } finally {
//       setLoading(false);
//     }
//   };

//   /* ---------------- RESET ---------------- */
//   const resetForm = () => {
//     setGuardFormData({
//       id: "",
//       name: "",
//       email: "",
//       gender: "",
//       pnNumber: "",
//       adharNo: "",
//       username: "",
//       password: "",
//       rank: "",
//       experience: "",
//       contactno: "",
//       picture: "",
//       status: "Inactive",
//     });
//     setOtherRank("");
//     setShowOtherRank(false);
//     setEmailError("");
//     setContactError("");
//     setIsEditMode(false);
//     setImageFile(null);
//   };

//   return (
//     <>
//       <style>{CSS}</style>

//       <div className="container">
//         <div className="registration-card">
//           {/* HEADER */}
//           <div className="header-section">
//             <div className="header-content">
//               <h1 className="header-title">
//                 {isEditMode ? "Edit Officer Details" : "Officer Registration Form"}
//               </h1>
//               <p className="header-description">
//                 {isEditMode
//                   ? "Update existing security officer information."
//                   : "Register new security personnel with complete details."}
//               </p>
//             </div>

//             <div className={`mode-display ${isEditMode ? "edit-mode" : ""}`}>
//               {isEditMode ? "Edit Mode" : "Registration Mode"}
//             </div>
//           </div>

//           {/* FORM */}
//           <div className="form-section">
//             <form onSubmit={handleSubmit}>
//               <div className="form-layout">
//                 {/* Regular Fields */}
//                 {[
//                   ["name", "Full Name *"],
//                   ["email", "Email *"],
//                   ["pnNumber", "PN Number *"],
//                   ["adharNo", "Aadhaar Number *"],
//                   ["username", "Username *"],
//                   ["password", "Password *"],
//                   ["experience", "Work Experience (Years) *"],
//                   ["contactno", "Contact Number *"],
//                 ].map(([n, l]) => (
//                   <div className="form-field" key={n}>
//                     <label className="field-label">{l}</label>
//                     <input
//                       type={n === "password" ? "password" : "text"}
//                       name={n}
//                       value={guardformData[n]}
//                       onChange={handleChange}
//                       required
//                     />
//                     {n === "email" && emailError && (
//                       <div className="error-message">{emailError}</div>
//                     )}
//                     {n === "contactno" && contactError && (
//                       <div className="error-message">{contactError}</div>
//                     )}
//                   </div>
//                 ))}

//                 {/* Gender */}
//                 <div className="form-field full-width-field">
//                   <label className="field-label">Gender *</label>
//                   <select
//                     name="gender"
//                     value={guardformData.gender}
//                     onChange={handleChange}
//                     required
//                   >
//                     <option value="">Select Gender</option>
//                     <option>Male</option>
//                     <option>Female</option>
//                     <option>Other</option>
//                   </select>
//                 </div>

//                 {/* Rank */}
//                 <div className="form-field full-width-field">
//                   <label className="field-label">Officer Rank *</label>
//                   <select
//                     name="rank"
//                     value={guardformData.rank}
//                     onChange={handleChange}
//                     required
//                   >
//                     <option value="">Select Rank</option>
//                     {ranks.map((r, i) => (
//                       <option key={i} value={r}>
//                         {r}
//                       </option>
//                     ))}
//                     <option value="Other">Other</option>
//                   </select>
//                 </div>

//                 {/* Other Rank Input */}
//                 {showOtherRank && (
//                   <div className="form-field full-width-field other-rank-wrapper">
//                     <label className="field-label">Custom Rank *</label>
//                     <input
//                       placeholder="Enter custom rank"
//                       value={otherRank}
//                       onChange={(e) => setOtherRank(e.target.value)}
//                       required
//                     />
//                   </div>
//                 )}

//                 {/* Profile Picture */}
//                 <div className="form-field full-width-field">
//                   <label className="field-label">Profile Picture {isEditMode ? "(Optional for update)" : "*"}</label>
//                   <input
//                     type="file"
//                     accept="image/*"
//                     onChange={handleImageSelect}
//                     required={!isEditMode}
//                   />
//                 </div>
//               </div>

//               <div className="actions-section">
//                 <button
//                   type="submit"
//                   className="submit-btn"
//                   disabled={loading}
//                 >
//                   {loading
//                     ? "Processing..."
//                     : isEditMode
//                     ? "Update Officer"
//                     : "Register Officer"}
//                 </button>

//                 {isEditMode && (
//                   <button
//                     type="button"
//                     className="cancel-btn"
//                     onClick={resetForm}
//                   >
//                     Cancel Update
//                   </button>
//                 )}
//               </div>
//             </form>
//           </div>
//         </div>
//       </div>
//     </>
//   );
// }

// /* ---------------- CONSISTENT CSS (same as VIPForm & updated UserForm) ---------------- */
// const CSS = `
// * { margin:0; padding:0; box-sizing:border-box; }

// .container { 
//   max-width:1100px; 
//   margin:20px auto; 
//   padding:20px; 
// }

// .registration-card {
//   background:linear-gradient(135deg, rgba(190,235,120,0.45), rgba(120,200,255,0.45));
//   border-radius:20px;
//   box-shadow:0 15px 40px rgba(0,0,0,.1);
//   overflow:hidden;
//   border:1px solid #e5e7eb;
// }

// .header-section {
//   background:linear-gradient(135deg,#1e3a8a,#3b82f6);
//   padding:35px 40px 45px;
//   color:#fff;
//   position:relative;
// }

// .header-content {
//   max-width: 700px;
// }

// .header-title { 
//   font-size:32px; 
//   font-weight:700; 
//   color:#fff; 
// }

// .header-description {
//   margin-top:10px;
//   font-size:16px;
//   opacity:0.9;
// }

// .mode-display {
//   position:absolute;
//   right:40px;
//   bottom:-18px;
//   background:#f59e0b;
//   padding:10px 22px;
//   border-radius:50px;
//   color:white;
//   font-weight:600;
//   font-size:14px;
// }

// .mode-display.edit-mode { 
//   background:#059669; 
// }

// .form-section { 
//   padding:50px 40px; 
// }

// .form-layout {
//   display:grid;
//   grid-template-columns:repeat(2,1fr);
//   gap:28px;
// }

// .form-field { 
//   display:flex; 
//   flex-direction:column; 
// }

// .full-width-field { 
//   grid-column:span 2; 
// }

// .other-rank-wrapper {
//   grid-column:span 2;
// }

// .field-label {
//   margin-bottom:8px;
//   font-weight:600;
//   color:#374151;
// }

// input, select {
//   padding:15px 20px;
//   border:2px solid #e5e7eb;
//   border-radius:12px;
//   font-size:16px;
// }

// input:focus, select:focus {
//   outline:none;
//   border-color:#3b82f6;
// }

// .error-message {
//   color:#ef4444;
//   font-size:13px;
//   margin-top:6px;
// }

// .actions-section {
//   margin-top:40px;
//   display:flex;
//   gap:12px;
// }

// .submit-btn {
//   flex:1;
//   background:#2563eb;
//   color:white;
//   padding:18px;
//   border:none;
//   border-radius:12px;
//   font-size:16px;
//   font-weight:600;
//   cursor:pointer;
// }

// .submit-btn:hover {
//   background:#1d4ed8;
// }

// .submit-btn:disabled {
//   background:#93c5fd;
//   cursor:not-allowed;
// }

// .cancel-btn {
//   background:#f3f4f6;
//   color:#374151;
//   padding:18px;
//   border-radius:12px;
//   border:2px solid #ddd;
//   font-size:16px;
//   font-weight:600;
//   cursor:pointer;
// }

// @media (max-width:768px) {
//   .form-layout {
//     grid-template-columns:1fr;
//   }
//   .full-width-field, .other-rank-wrapper {
//     grid-column:span 1;
//   }
//   .mode-display {
//     position:static;
//     display:inline-block;
//     margin-top:20px;
//   }
// }
// `;